import { compactPair, fullNumber, fullPair, levelPercent, type LevelView } from "@/lib/level";
import type { SmarticoLevel } from "./types";

/* Where the player sits on Smartico's ladder: the highest level whose XP floor
   they have passed, and the one after it.

   Sorted by `ordinal_position` rather than by `required_points`, because the
   position is what Smartico calls the order — two levels can share a floor, and
   the operator's intended sequence is the one to trust.

   Kept pure so the ladder maths is testable without a browser or a label, the
   same split `api/mission-progress.ts` uses. */
export const smarticoLevelView = (
  levels: SmarticoLevel[] | undefined,
  xp: number,
): LevelView | null => {
  if (!levels || levels.length === 0) return null;

  const ladder = [...levels].sort((left, right) => left.ordinal_position - right.ordinal_position);

  let index = 0;

  for (const [position, level] of ladder.entries()) {
    if (xp >= level.required_points) {
      index = position;
      continue;
    }

    break;
  }

  const current = ladder[index];
  const next = ladder.at(index + 1) ?? null;

  /* At the top of the ladder there is nothing left to progress towards, so the
     bar is full rather than measured against a target that does not exist. */
  const floor = current.required_points;
  const ceiling = next === null ? current.required_points : next.required_points;

  const percent = next === null ? 100 : levelPercent(xp - floor, ceiling - floor);

  return {
    name: current.name,
    nextName: next === null ? null : next.name,
    imageUrl: current.image || null,
    nextImageUrl: next === null ? null : next.image || null,
    percent,
    label: `${compactPair(xp, ceiling)} XP`,
    exact: `${fullPair(xp, ceiling)} XP`,
    toNext: next !== null && ceiling - xp > 0 ? `${fullNumber(ceiling - xp)} XP to ${next.name}` : null,
    spoken:
      next === null
        ? `${current.name}, the top level, with ${xp} XP`
        : `${current.name} — ${compactPair(xp, ceiling)} XP, ${Math.round(percent)}% to ${next.name}`,
  };
};
